import { useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom"; // ✅ Correct hooks for non-Next.js

import { paths } from "src/routes/paths";
// locales
import { useLocales } from "src/locales";
// components
import Label from "src/components/label";
import Iconify from "src/components/iconify";
import SvgColor from "src/components/svg-color";
import {
  useGetAllCompany,
  useGetServiceUser,
  useGetUserById,
} from "src/sections/user/users/getusers";

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor
    src={`/assets/icons/navbar/${name}.svg`}
    sx={{ width: 1, height: 1 }}
  />
);

const ICONS = {
  job: icon("ic_job"),
  blog: icon("ic_blog"),
  chat: icon("ic_chat"),
  mail: icon("ic_mail"),
  user: icon("ic_user"),
  file: icon("ic_file"),
  lock: icon("ic_lock"),
  tour: icon("ic_tour"),
  order: icon("ic_order"),
  label: icon("ic_label"),
  blank: icon("ic_blank"),
  kanban: icon("ic_kanban"),
  folder: icon("ic_folder"),
  banking: icon("ic_banking"),
  booking: icon("ic_booking"),
  invoice: icon("ic_invoice"),
  product: icon("ic_product"),
  calendar: icon("ic_calendar"),
  disabled: icon("ic_disabled"),
  external: icon("ic_external"),
  menuItem: icon("ic_menu_item"),
  ecommerce: icon("ic_ecommerce"),
  analytics: icon("ic_analytics"),
  dashboard: icon("ic_dashboard"),
};

// ----------------------------------------------------------------------

// Emails allowed to see/use the Threat Intelligence module during the staging
// trial. Everyone else has it hidden from the nav and is redirected to 404 if
// they deep-link into any /threatIntelligence route. Add trial users here.
const THREAT_INTEL_ALLOWED_EMAILS = ["gurukiransn@beyondcloud.org.in" , "fardeen@beyondcloud.org.in" , "customer@gobeyond.local"];

export function useNavData() {
  const { t } = useLocales();
  const location = useLocation();
  const navigate = useNavigate();

  const companyId = localStorage.getItem("userCompanyId") as any;

  // console.log("-----------------companyId-----",companyId)

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .replace(/[\/\\]/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const { services } = useGetServiceUser(1, 1000);
  const { getuserbyid } = useGetUserById() as any;
  const { getCompanyid } = useGetAllCompany() as any;

  //  console.log("-----------------getCompanyid-----",getCompanyid)

  const companyIdsArray: string[] =
    getCompanyid?.data?.map((c: any) => String(c.companyid)) || [];

  const hasAccess = companyIdsArray.includes(companyId);

  const rolesformstorage = getuserbyid?.user?.role;

  // Threat Intelligence allowlist gate (by email, case-insensitive). While the
  // user record is still loading we don't gate — avoids bouncing an allowed
  // user to 404 before their email is known.
  const userEmail = (getuserbyid?.user?.email || "").trim().toLowerCase();
  const userLoaded = Boolean(getuserbyid?.user);
  const canAccessThreatIntel = THREAT_INTEL_ALLOWED_EMAILS.map((e) =>
    e.toLowerCase(),
  ).includes(userEmail);

  const serviceEnabled = getuserbyid?.user?.services_enabled || {
    brand: [],
    threat: [],
  };

  const companyIds = getuserbyid?.user?.companies?.map((company: any) =>
    String(company.id),
  );

  const allowedCompanyIds = ["55"];

  const hasPermission = companyIds?.some((id: any) =>
    allowedCompanyIds.includes(id),
  );

  // console.log("hasPermission---------",hasPermission)

  // console.log("-----------------companyIds-----",companyIds)
  const filteredServices = useMemo(
    () => (Array.isArray(services) ? services : []),
    [services],
  );

  const filteredBrandServices = useMemo(
    () =>
      rolesformstorage === "2"
        ? filteredServices.filter(
            (s) =>
              s.category === "brand" && serviceEnabled?.brand?.includes(s.id),
          )
        : filteredServices.filter((s) => s.category === "brand"),
    [filteredServices, rolesformstorage, serviceEnabled?.brand],
  );

  const filteredThreatServices = useMemo(
    () =>
      rolesformstorage === "2"
        ? filteredServices.filter(
            (s) =>
              s.category === "threat" && serviceEnabled?.threat?.includes(s.id),
          )
        : filteredServices.filter((s) => s.category === "threat"),
    [filteredServices, rolesformstorage, serviceEnabled?.threat],
  );

  // -------------------- Role 2 Route Blocking (Redirect to 404) --------------------

  const allowedPathsForRole2 = useMemo(() => {
    const brandPaths = filteredBrandServices.map(
      (s) =>
        `${paths.dashboard.general.intelligence}/${s.id}/${slugify(s.name)}`,
    );
    const threatPaths = filteredThreatServices.map(
      (s) =>
        `${paths.dashboard.general.intelligenceTherat}/${s.id}/${slugify(
          s.name,
        )}`,
    );
    const takedownPaths = filteredServices.map(
      (s) =>
        `${paths.dashboard.takedownrequest.root}/${s.id}/${slugify(s.name)}`,
    );
    return [
      paths.dashboard.root,
      paths.threatIntelligence.root,
      paths.dashboard.report.root,
      paths.dashboard.report.vendorPerformance,
      paths.dashboard.report.darkwebmentions,
      paths.dashboard.report.socialmediadetection,
      paths.dashboard.general.generatedApiView,
      ...brandPaths,
      ...threatPaths,
      ...takedownPaths,
    ];
  }, [filteredBrandServices, filteredThreatServices, filteredServices]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (rolesformstorage === "2") {
      const isAllowed = allowedPathsForRole2.some(
        (path) => currentPath?.startsWith(path),
      );
      if (!isAllowed) {
        navigate("/404", { replace: true });
      }
    }
  }, [location.pathname, rolesformstorage, allowedPathsForRole2, navigate]);

  // Threat Intelligence trial allowlist: a non-allowed user who deep-links into
  // (or is already sitting on) any /threatIntelligence route is sent to 404.
  useEffect(() => {
    if (
      userLoaded &&
      !canAccessThreatIntel &&
      location.pathname?.startsWith(paths.threatIntelligence.root)
    ) {
      navigate("/404", { replace: true });
    }
  }, [location.pathname, userLoaded, canAccessThreatIntel, navigate]);

  // ---------------------- Navigation Data ----------------------

  const data = useMemo(
    () => [
      {
        subheader: t("Menu"),
        items: [
          // {
          //   title: t('Dashboard'),
          //   path: paths.dashboard.root,
          //   icon: ICONS.dashboard,
          // },

          ...(rolesformstorage !== "3"
            ? [
                {
                  title: t("Dashboard"),
                  path: paths.dashboard.root,
                  icon: ICONS.dashboard,
                },
              ]
            : []),

          ...(rolesformstorage !== "2"
            ? [
                {
                  title: t("companies"),
                  path: paths.dashboard.general.company,
                  icon: ICONS.banking,
                },
              ]
            : []),

          ...(rolesformstorage !== "2" && rolesformstorage !== "3"
            ? [
                {
                  title: t("bulk upload"),
                  path: paths.dashboard.general.bulkUploadPage,
                  icon: ICONS.folder,
                },
              ]
            : []),

          ...(rolesformstorage !== "2"
            ? [
                {
                  title: t("users"),
                  path: paths.dashboard.users.users,
                  icon: ICONS.external,
                },
              ]
            : []),

          ...(rolesformstorage !== "2" && rolesformstorage !== "3"
            ? [
                {
                  title: t("Roles"),
                  path: paths.dashboard.general.rolesCreateView,
                  icon: ICONS.job,
                },
              ]
            : []),

          ...(rolesformstorage !== "2" && rolesformstorage !== "3"
            ? [
                {
                  title: t("Service"),
                  path: paths.dashboard.Service.ServicePage,
                  icon: ICONS.file,
                },
              ]
            : []),

          // ...(rolesformstorage === '2'
          //   ? [{
          //     title: t('Schedule Report User'),
          //     path: paths.dashboard.userreport.userreport,
          //     icon: ICONS.banking,
          //   }] : []),

          ...(rolesformstorage !== "3"
            ? [
                {
                  title: t("report"),
                  path: paths.dashboard.report.root,
                  icon: ICONS.analytics,
                  children: [
                    {
                      title: t("company report"),
                      path: paths.dashboard.report.root,
                    },

                    ...((rolesformstorage === "1" && companyId === "1") ||
                    (hasPermission && ["2"].includes(rolesformstorage))
                      ? [
                          {
                            title: t("Vendor Performance"),
                            path: paths.dashboard.report.vendorPerformance,
                          },
                        ]
                      : []),

                    ...(rolesformstorage === '1' || rolesformstorage === '2'
                      ? [{ title: t('Schedule Report'), path: paths.dashboard.report.schedulereport }]
                      : []
                    ),

                    {
                      title: t("takedown request vs completed"),
                      path: paths.dashboard.report.takedownrequestlist,
                    },
                    {
                      title: t("detection by module vs total"),
                      path: paths.dashboard.report.detectionbymodulelist,
                    },
                    {
                      title: t("ModuleWise Breakup"),
                      path: paths.dashboard.report.modulewisebreakuplist,
                    },
                    {
                      title: t("Tat For TakeDowns(commulatives)"),
                      path: paths.dashboard.report.tatfortakedownslist,
                    },
                    {
                      title: t("Average TakeDown Time"),
                      path: paths.dashboard.report.averagetakedowntime,
                    },
                    {
                      title: t("Cumulative Takedown Trend"),
                      path: paths.dashboard.report.cumulativetakedowntrend,
                    },
                    {
                      title: t("Social Media Detection"),
                      path: paths.dashboard.report.socialmediadetection,
                    },

                    ...(["1", "2"].includes(rolesformstorage) &&
                    filteredThreatServices.length > 0
                      ? [
                          {
                            title: t("Dark Web Mentions"),
                            path: paths.dashboard.report.darkwebmentions,
                          },
                        ]
                      : []),
                  ],
                },
              ]
            : []),

          ...(rolesformstorage !== "3" && filteredBrandServices.length > 0
            ? [
                {
                  title: t("Brand intelligence"),
                  path: paths.dashboard.general.intelligence,
                  icon: ICONS.menuItem,
                  children: filteredBrandServices.map((service) => ({
                    title: service.name,
                    path: `${paths.dashboard.general.intelligence}/${
                      service.id
                    }/${slugify(service.name)}`,
                  })),
                },
              ]
            : []),

          ...(rolesformstorage !== "3" && filteredThreatServices.length > 0
            ? [
                {
                  title: t("Darkweb Monitoring"),
                  path: paths.dashboard.general.intelligenceTherat,
                  icon: ICONS.menuItem,
                  children: filteredThreatServices.map((service) => ({
                    title: service.name,
                    path: `${paths.dashboard.general.intelligenceTherat}/${
                      service.id
                    }/${slugify(service.name)}`,
                  })),
                },
              ]
            : []),

          ...(canAccessThreatIntel
            ? [
          {
            title: t("Threat Intelligence"),
            path: paths.threatIntelligence.root,
            icon: ICONS.label,
            children: [
              {
                title: t("Telegram Feeds"),
                path: paths.threatIntelligence.telegramFeeds,
              },
              {
                title: t("Source Management"),
                path: paths.threatIntelligence.sourceManagement,
              },
              {
                title: t("Ransomware Intelligence"),
                path: paths.threatIntelligence.ransomware.root,
                children: [
                  // Overview + search
                  {
                    title: t("Overview"),
                    path: paths.threatIntelligence.ransomware.root,
                  },
                  {
                    title: t("Search"),
                    path: paths.threatIntelligence.ransomware.search,
                  },
                  // watchlist disabled for now
                  // {
                  //   title: t("Watchlist"),
                  //   path: paths.threatIntelligence.ransomware.watchlist,
                  // },
                  // Entities
                  {
                    title: t("Threat Actors"),
                    path: paths.threatIntelligence.ransomware.groups,
                  },
                  {
                    title: t("Victim Claims"),
                    path: paths.threatIntelligence.ransomware.victims,
                  },
                  {
                    title: t("Industries"),
                    path: paths.threatIntelligence.ransomware.sectors,
                  },
                  {
                    title: t("Geographies"),
                    path: paths.threatIntelligence.ransomware.geo,
                  },
                  // Investigation
                  {
                    title: t("Campaigns"),
                    path: paths.threatIntelligence.ransomware.campaigns,
                  },
                  {
                    title: t("Correlation Graph"),
                    path: paths.threatIntelligence.ransomware.graph,
                  },
                  // Technical intelligence
                  {
                    title: t("Vulnerabilities & Tooling"),
                    path: paths.threatIntelligence.ransomware.threatIntel,
                  },
                  {
                    title: t("ATT&CK & Indicators"),
                    path: paths.threatIntelligence.ransomware.mitre,
                  },
                ],
              },
            ],
          },
              ]
            : []),

          ...(rolesformstorage === "2" && hasAccess
            ? [
                {
                  title: t("Attack Surface Management"),
                  path: paths.dashboard.asm.root,
                  icon: ICONS.analytics,
                  children: [
                    { title: t("issues"), path: paths.dashboard.asm.root },
                    { title: t("assets"), path: paths.dashboard.asm.assets },
                    { title: t("port"), path: paths.dashboard.asm.portlist },
                    {
                      title: t("report"),
                      path: paths.dashboard.asm.reportlist,
                    },
                  ],
                },
              ]
            : []),

          ...(rolesformstorage !== "2" && rolesformstorage !== "3"
            ? [
                {
                  title: t("TakeDown Request"),
                  path: paths.dashboard.takedownrequest.root,
                  icon: ICONS.tour,
                  children: filteredServices.map((service) => ({
                    title: service.name,
                    path: `${paths.dashboard.takedownrequest.root}/${
                      service.id
                    }/${slugify(service.name)}`,
                  })),
                },
              ]
            : []),

          // {
          //   title: t('generate API token'),
          //   path: paths.dashboard.general.generatedApiView,
          //   icon: ICONS.banking,
          // },

          // {
          //   title: t('social media chart'),
          //   path: paths.dashboard.socialmediatree.socialmediatree,
          //   icon: ICONS.banking,
          // },

          ...(rolesformstorage !== "3"
            ? [
                {
                  title: t("generate API token"),
                  path: paths.dashboard.general.generatedApiView,
                  icon: ICONS.banking,
                },
              ]
            : []),

          ...(rolesformstorage !== "3"
            ? [
                {
                  title: t("social media chart"),
                  path: paths.dashboard.socialmediatree.socialmediatree,
                  icon: ICONS.banking,
                },
              ]
            : []),

          ...(rolesformstorage === "1"
            ? [
                {
                  title: t("Login History"),
                  path: paths.dashboard.LoginHistory.loginhistory,
                  icon: ICONS.banking,
                },
              ]
            : []),

          //  {
          //   title: t('Dark Web Threat Feeds'),
          //   path: paths.dashboard.DarkWebMentionThreat.darkwebmentionthreat,
          //   icon: ICONS.analytics,
          // },

          ...(rolesformstorage === "2" && companyId === "48"
            ? [
                {
                  title: t("Dark Web Threat Feeds"),
                  path: paths.dashboard.DarkWebMentionThreat
                    .darkwebmentionthreat,
                  icon: ICONS.analytics,
                },
              ]
            : []),

          // ...(rolesformstorage === '1' && companyId === '1'
          //   ? [
          //         {
          //             title: t('ASM Permission'),
          //             path: paths.dashboard.AsmPermission.AsmviewList,
          //             icon: ICONS.banking,
          //           },
          //     ]
          //   : []),
        ],
      },
    ],
    [
      t,
      filteredServices,
      filteredBrandServices,
      filteredThreatServices,
      rolesformstorage,
      hasPermission,
      companyId,
      hasAccess,
      canAccessThreatIntel,
    ],
  );

  return data;
}
